/**
 * @typedef {import('BMapsModel').Compound} Compound
 * @typedef {import('BMapsModel').MapCase} MapCase
 * @typedef {import('BMapsModel').ParentCompound} ParentCompound
 * @typedef {import('@mui/x-data-grid').GridColumnVisibilityModel} GridColumnVisibilityModel
 * @typedef {import('@mui/x-data-grid').GridRowsProp} GridRowsProp
 * @typedef {import('@mui/x-data-grid').GridColDef} GridColDef
 * @typedef {import('@mui/x-data-grid').GridColumnGroupingModel} GridColumnGroupingModel
 * @typedef {import('@mui/x-data-grid').GridToolbarProps} GridToolbarProps
 * @typedef {import('@mui/x-data-grid').ToolbarPropsOverrides} ToolbarPropsOverrides
 * @typedef {any} item
 * @typedef {import('@mui/x-data-grid').GridSlotProps['filterPanel']} FilterPanelProps
 */
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    DataGrid, GridColumnHeaderSortIcon, GridColumnsPanel,
    GridFilterPanel, useGridApiRef,
} from '@mui/x-data-grid';
import {
    Directionality,
    getSortComparatorForDirectionality, getSortingOrderForDirectionality,
} from 'BMapsSrc/integrations/properties_common';
import { GridToolBar } from './CustomToolbar';
import BMapsTooltip from '../BMapsTooltip';

export const defaultPrecision = 2;

/**
 * @param {{
 *     rows: GridRowsProp,
 *     columns: GridColDef[],
 *     columnGroupingModel?: GridColumnGroupingModel,
 *     setColumnVisibilityModel?: (columnVisibilityModel: GridColumnVisibilityModel) => any,
 *     columnVisibilityModel?: GridColumnVisibilityModel,
 *     setCheckedRowItems?: (item: item) => any,
 *     rowItems?: item[],
 *     disableTable?: boolean
 *     exportFileName?: string
 *     checkboxSelection?: boolean
 *     toolBarId? string,
 *     customPanel?: (props: GridColumnsPanelProps) => any
 *     visibilityDefault?: boolean
 *     duplicateColumnKeys?: string[]
 *     initialRowSelectionModel?: any[]
 *     rowNoun?: string
 *}} props
 * @returns
 */
// In MUI X DataGrid v9, rowSelectionModel changed from GridRowId[] to
// { type, ids: Set<GridRowId> }.
const toSelectionModel = (ids) => ({ type: 'include', ids: new Set(ids || []) });

export function BMapsTable({
    rows, columns, columnGroupingModel, initialRowSelectionModel=[],
    setColumnVisibilityModel, columnVisibilityModel,
    setCheckedRowItems, rowItems, disableTable, exportFileName,
    checkboxSelection, toolBarId, customPanel = null, visibilityDefault = false,
    duplicateColumnKeys = [], availableCharts, setChart, rowNoun='row',
}) {
    const apiRef = useGridApiRef();
    const [rowSelectionModel, setRowSelectionModel] = useState(
        () => toSelectionModel(initialRowSelectionModel)
    );

    useEffect(() => {
        setRowSelectionModel(toSelectionModel(initialRowSelectionModel));
    }, [initialRowSelectionModel]);

    const managedVisibilityModel = manageColumnVisibility(
        columnVisibilityModel, columns, duplicateColumnKeys, visibilityDefault
    );

    // The community (MIT) DataGrid always paginates and caps pageSize at 100
    // (the cap is not exported by the library). We pin the page size to that
    // cap explicitly below, so this same constant drives both the grid's page
    // size and the footer logic. When there is more than one page of rows the
    // footer must be shown so the page controls are reachable; otherwise rows
    // past the first page are hidden. Smaller tables keep the footer-less look.
    // If MUI DataGrid changes to where maxRowsPerPage is not allowed, this may throw.
    const maxRowsPerPage = 100;
    const hideTableFooter = rows.length <= maxRowsPerPage;
    return (
        <DataGrid
            rows={rows}
            columns={columns}
            columnGroupingModel={columnGroupingModel}
            checkboxSelection={checkboxSelection}
            disableRowSelectionOnClick
            showToolbar={!!toolBarId}
            slots={{
                toolbar: toolBarId ? (toolBarProps) => (
                    <GridToolBar
                        {...toolBarProps}
                        exportFileName={exportFileName}
                        toolBarId={toolBarId}
                        charts={availableCharts}
                        setChart={setChart}
                    />
                ) : undefined,
                filterPanel: () => (
                    <CustomFilterPanel />
                ),
                filterPanelDeleteIcon: () => (
                    <DeleteIcon />
                ),
                columnsPanel: customPanel ? (props) => customPanel(props)
                    : (props) => <GridColumnsPanel {...props} />,
                columnHeaderSortIcon: (props) => {
                    const found = columns.find((c) => c.field === props.field);
                    const title = found ? `Sort by ${found.headerName}` : 'Sort';
                    return (
                        <BMapsTooltip title={title} noMaxWidth>
                            <GridColumnHeaderSortIcon {...props} />
                        </BMapsTooltip>
                    );
                },
            }}
            slotProps={{
                basePopper: { placement: 'bottom-start' },
                columnsManagement: {
                    getTogglableColumns: (cols) => cols.filter(({ field }) => {
                        // '__check__' is the default name for the checkboxes given by
                        // mui data grid. This will prevent users from removing the
                        // checkbox column
                        if (field === '__check__') return false;
                        return true;
                    }).map((col) => col.field),
                },
            }}
            hideFooter={hideTableFooter}
            pageSizeOptions={[maxRowsPerPage]}
            initialState={{ pagination: { paginationModel: { pageSize: maxRowsPerPage } } }}
            localeText={{
                paginationDisplayedRows: ({ from, to, count }) => (
                    `${from}–${to} of ${count} ${count === 1 ? rowNoun : `${rowNoun}s`}`
                ),
            }}
            apiRef={apiRef}
            loading={disableTable}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(gridRows) => {
                const newCheckedItems = [];
                gridRows.ids.forEach((index) => {
                    if (rowItems[index]) {
                        newCheckedItems.push(rowItems[index]);
                    }
                });
                setCheckedRowItems(newCheckedItems);
                setRowSelectionModel(gridRows);
            }}
            onColumnVisibilityModelChange={(event) => {
                setColumnVisibilityModel(event);
            }}
            columnVisibilityModel={managedVisibilityModel}
            sx={{
                '& .protein-header': {
                    backgroundColor: 'lightGray',
                    borderRight: '1px solid white',
                    borderLeft: '1px solid white',
                },
                '& .MuiDataGrid-columnHeader--emptyGroup': {
                    borderBottom: '1px solid lightgray',
                    '& .MuiDataGrid-columnSeparator': {
                        display: 'none',
                    },
                },
                '& .none-header': {
                    padding: 0,
                },
                // on some resolutions this separator causes the top check box to become
                // miss-aligned.
                '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnSeparator': {
                    display: 'none',
                },
                // Changes for custom header menu button
                // 1. Hide the default menu icon button
                //    Hidden instead of disabled because the disabled flag
                //    is consulted when displaying the "hide column" menu option.
                '& .MuiDataGrid-menuIconButton': { display: 'none' },
                // 2. Display the custom menu button
                //    Only appears on hover of column header OR when the menu is expanded.
                //    Hidden with width+visibility, instead of display, to avoid jumping tooltip.
                '& .compoundTable-customMenuButton': {
                    width: 0,
                    visibility: 'hidden',
                },
                '& .MuiDataGrid-columnHeader:hover': {
                    '.compoundTable-customMenuButton': {
                        width: 'unset',
                        visibility: 'visible',
                    },
                },
                '& .MuiDataGrid-columnHeader .compoundTable-customMenuButton[aria-expanded="true"]': {
                    width: 'unset',
                    visibility: 'visible',
                },
                // 3. Make sure sort icon is visible when menu is open, not just on hover.
                //    This is the default behavior when using the default column menu.
                //    The columnHeader needs to adjust visibility and width,
                //    and the button itself need to adjust opacity.
                '& .MuiDataGrid-columnHeader:has(*[aria-expanded="true"]) .MuiDataGrid-iconButtonContainer:has(.MuiDataGrid-sortButton)': {
                    visibility: 'visible',
                    width: 'unset',
                },
                // Opacity depends on whether the column has an active sort (100%) or not (50%)
                '& .MuiDataGrid-columnHeader:has(*[aria-expanded="true"]) .MuiDataGrid-sortButton': {
                    opacity: 1,
                },
                '& .MuiDataGrid-columnHeader:has(*[aria-expanded="true"]):not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortButton': {
                    opacity: 0.5,
                },
            }}
        />
    );
}
function CustomFilterPanel() {
    const filterPanelProps = {
        // Display columns by ascending alphabetical order
        columnsSort: 'asc',
        sx: {
            // Customize inputs using css selectors
            '& .MuiDataGrid-filterForm:nth-child(even)': {
                backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#444' : '#f5f5f5'),
            },
            '& .MuiDataGrid-filterFormLogicOperatorInput': { mr: 2 },
            '& .MuiDataGrid-filterFormColumnInput': { mr: 2, width: 150 },
            '& .MuiDataGrid-filterFormOperatorInput': { mr: 2 },
            '& .MuiDataGrid-filterFormValueInput': { width: 200 },
            '& .MuiIconButton-root': {
                margin: 'auto',
            },
        },
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <GridFilterPanel {...filterPanelProps} />
        </Box>
    );
}

// Provides additional functionality to column visibility
// columns can be hidden by default if visibilityDefault is false
// column visibility can be linked to other columns by strings that are common in multiple columns
function manageColumnVisibility(
    compoundVisibilityModel, allColumns, duplicateColumnKeys, visibilityDefault
) {
    const managedVisibilityModel = { ...compoundVisibilityModel };
    const refrenceVisibility = {};

    Object.keys(managedVisibilityModel).forEach((key) => {
        const refrenceTitle = duplicateColumnKeys.find((title) => key.includes(title));
        if (refrenceTitle) {
            refrenceVisibility[refrenceTitle] = managedVisibilityModel[key];
        }
    });

    allColumns.forEach((column) => {
        Object.keys(refrenceVisibility).forEach((key) => {
            if (column.field.includes(key)) {
                managedVisibilityModel[column.field] = refrenceVisibility[key];
            }
        });
    });

    if (!visibilityDefault) {
        allColumns.forEach((column) => {
            if (!managedVisibilityModel[column.field]) {
                managedVisibilityModel[column.field] = false;
            }
        });
    }

    return managedVisibilityModel;
}

/**
     * @param {string | number} value
     * @returns
     */
export function getFormattedValue(value, precision) {
    let formattedValue = value;
    if (formattedValue == null) return value;
    if (typeof formattedValue !== 'number') {
        formattedValue = Number(formattedValue);
        if (Number.isNaN(formattedValue)) {
            return value;
        }
    }
    if (precision != null) {
        return formattedValue.toFixed(precision);
    }
    // Default behavior shows integers without decimal places
    if (Number.isInteger(formattedValue)) return formattedValue;
    return formattedValue.toFixed(defaultPrecision);
}

/**
 * Apply directionality-aware sorting to a column definition (mutated in place).
 *
 * Numeric columns receive an empty-value-aware sort comparator, so blank cells
 * sort consistently. Columns without explicit directionality metadata (e.g.
 * imported docking scores) default to "minimize", so blank cells sort to the
 * bottom.
 *
 * String columns keep MUI's default string comparator *unless* the property
 * declares a directionality, in which case the column opts in to value-based
 * sorting (its values must then be numerically comparable).
 *
 * @param {GridColDef} columnInfo
 * @param {string} [directionality] - a Directionality value, or null/undefined
 */
export function applyColumnDirectionality(columnInfo, directionality) {
    if (columnInfo.type === 'string' && directionality == null) return;
    const effectiveDirectionality = directionality ?? Directionality.MINIMIZE;
    columnInfo.sortComparator = getSortComparatorForDirectionality(effectiveDirectionality);
    columnInfo.sortingOrder = getSortingOrderForDirectionality(effectiveDirectionality);
}
